import React, { useState, useEffect, useCallback } from 'react';
import {
    ShieldCheck, Loader2, AlertTriangle, Download, RefreshCw,
    CheckCircle2, XCircle, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient, DbsCheck, Driver, InitiateDbsCheckRequest, DbsCheckType } from '../../services/api';
import DbsStatusBadge from './DbsStatusBadge';
import DbsStatusTimeline from './DbsStatusTimeline';
import RequestDbsModal from './RequestDbsModal';
import InitiateDbsModal from './InitiateDbsModal';

interface DbsTabProps {
    driver: Driver;
    orgName: string;
    isAdmin: boolean;
    currentUserName?: string;
    onDriverUpdated?: () => void;
    isDemoMode?: boolean;
}

function formatDate(value?: string | null): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const DbsTab: React.FC<DbsTabProps> = ({ driver, orgName, isAdmin, currentUserName = '', onDriverUpdated, isDemoMode = false }) => {
    const [checks, setChecks] = useState<DbsCheck[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showInitiateModal, setShowInitiateModal] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    const latestCheck = checks[0] ?? null;

    const loadChecks = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getDbsChecksForDriver(driver.id);
            setChecks(response.checks ?? []);
        } catch (err: any) {
            setError(err.message || 'Failed to load DBS checks');
        } finally {
            setIsLoading(false);
        }
    }, [driver.id]);

    useEffect(() => {
        loadChecks();
    }, [loadChecks]);

    const handleRequestDbs = async (checkType: DbsCheckType) => {
        try {
            await apiClient.requestDbsCheck(driver.id, checkType);
            toast.success('DBS invite sent to driver');
            setShowRequestModal(false);
            await loadChecks();
            onDriverUpdated?.();
        } catch (err: any) {
            toast.error(err.message || 'Failed to send DBS invite');
        }
    };

    const handleInitiateDbs = async (payload: InitiateDbsCheckRequest) => {
        if (!latestCheck) return;
        try {
            await apiClient.initiateDbsCheck(latestCheck.id, payload);
            toast.success('DBS check initiated');
            setShowInitiateModal(false);
            await loadChecks();
            onDriverUpdated?.();
        } catch (err: any) {
            toast.error(err.message || 'Failed to initiate DBS check');
        }
    };

    const handleDownloadPdf = async () => {
        if (!latestCheck) return;
        try {
            setIsDownloadingPdf(true);
            const blob = await apiClient.downloadDbsPdf(latestCheck.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `DBS-${driver.firstName}-${driver.lastName}-${latestCheck.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            toast.error(err.message || 'Failed to download PDF');
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    const cardClass = 'bg-gray-50/80 rounded-lg border border-gray-100 p-4';

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#69BE28' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                    <button onClick={loadChecks} className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // No checks yet
    if (!latestCheck) {
        return (
            <div className="p-6 space-y-4">
                {isDemoMode && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-blue-900">Demo Mode: View Only</p>
                            <p className="text-sm text-blue-800 mt-0.5">
                                DBS checks cannot be requested in demo mode. Contact Sales to enable this feature.
                            </p>
                        </div>
                    </div>
                )}
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="p-4 rounded-2xl mb-3" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                        <ShieldCheck className="w-9 h-9" style={{ color: '#69BE28' }} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">No DBS check on record</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-xs">
                        Request a DBS check to send an identity verification link to this driver.
                    </p>
                    {isAdmin && !isDemoMode && (
                        <button
                            onClick={() => setShowRequestModal(true)}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            <ShieldCheck className="w-4 h-4" />
                            Request DBS Check
                        </button>
                    )}
                </div>

                {showRequestModal && (
                    <RequestDbsModal
                        isOpen={showRequestModal}
                        onClose={() => setShowRequestModal(false)}
                        onConfirm={handleRequestDbs}
                        driver={driver}
                    />
                )}
            </div>
        );
    }

    const isComplete = latestCheck.status === 'app_complete';
    const isActionRequired = latestCheck.status === 'awaiting_initiation';
    const isRejected = latestCheck.status === 'app_rejected' || latestCheck.status === 'app_withdrawn';
    const canRequestNew = isComplete || isRejected || latestCheck.isLinkExpired;

    return (
        <div className="p-4 space-y-4">

            {/* Status strip */}
            <div className={cardClass}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">DBS Status</h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={loadChecks}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Refresh DBS checks"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Status banner */}
                {isComplete ? (
                    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg ${latestCheck.clear ? 'bg-green-600' : 'bg-red-600'}`}>
                        {latestCheck.clear
                            ? <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                            : <XCircle className="w-4 h-4 text-white flex-shrink-0" />
                        }
                        <span className="text-sm font-bold text-white">
                            {latestCheck.clear ? 'DBS Clear' : 'DBS Not Clear'}
                        </span>
                    </div>
                ) : isActionRequired ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500">
                        <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
                        <span className="text-sm font-bold text-white">Action Required: Initiate DBS Check</span>
                    </div>
                ) : isRejected ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-600">
                        <XCircle className="w-4 h-4 text-white flex-shrink-0" />
                        <span className="text-sm font-bold text-white capitalize">{latestCheck.status.replace('_', ' ')}</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600">
                        <ShieldCheck className="w-4 h-4 text-white flex-shrink-0" />
                        <span className="text-sm font-bold text-white">Check in progress</span>
                    </div>
                )}
            </div>

            {/* Check details */}
            <div className={cardClass}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Check Details</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">

                    <div>
                        <p className="text-xs text-gray-500">Type</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">{latestCheck.checkType}</p>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <div className="mt-0.5"><DbsStatusBadge status={latestCheck.status} clear={latestCheck.clear} /></div>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500">Requested</p>
                        <p className="text-sm font-medium text-gray-900">{formatDate(latestCheck.createdAt)}</p>
                    </div>

                    {latestCheck.completedAt && (
                        <div>
                            <p className="text-xs text-gray-500">Completed</p>
                            <p className="text-sm font-medium text-gray-900">{formatDate(latestCheck.completedAt)}</p>
                        </div>
                    )}

                    {latestCheck.initiatedBy && (
                        <div>
                            <p className="text-xs text-gray-500">Initiated by</p>
                            <p className="text-sm font-medium text-gray-900">{latestCheck.initiatedBy}</p>
                        </div>
                    )}

                    {latestCheck.route && (
                        <div>
                            <p className="text-xs text-gray-500">Verification route</p>
                            <p className="text-sm font-medium text-gray-900 capitalize">{latestCheck.route}</p>
                        </div>
                    )}

                    {latestCheck.dbsReference && (
                        <div className="col-span-2">
                            <p className="text-xs text-gray-500">DBS Reference</p>
                            <p className="text-sm font-medium font-mono text-gray-900">{latestCheck.dbsReference}</p>
                        </div>
                    )}

                    {latestCheck.certificateNumber && (
                        <div className="col-span-2">
                            <p className="text-xs text-gray-500">Certificate Number</p>
                            <p className="text-sm font-medium font-mono text-gray-900">{latestCheck.certificateNumber}</p>
                        </div>
                    )}

                </div>
            </div>

            {/* Progress timeline */}
            <div className={cardClass}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Progress</h3>
                <DbsStatusTimeline status={latestCheck.status} isRejected={isRejected} />
            </div>

            {/* Actions */}
            {isDemoMode ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-blue-900">Demo Mode: View Only</p>
                        <p className="text-sm text-blue-800 mt-0.5">
                            DBS checks cannot be requested or initiated in demo mode. Contact Sales to enable this feature.
                        </p>
                    </div>
                </div>
            ) : isAdmin && (
                <div className={cardClass}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Actions</h3>
                    <div className="flex flex-wrap gap-2">

                        {isActionRequired && (
                            <button
                                onClick={() => setShowInitiateModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                <ShieldCheck className="w-4 h-4" />
                                Initiate DBS Check
                            </button>
                        )}

                        {isComplete && (
                            <button
                                onClick={handleDownloadPdf}
                                disabled={isDownloadingPdf}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isDownloadingPdf
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <Download className="w-4 h-4" />
                                }
                                {isDownloadingPdf ? 'Downloading...' : 'Download Certificate'}
                            </button>
                        )}

                        {canRequestNew && (
                            <button
                                onClick={() => setShowRequestModal(true)}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                <ShieldCheck className="w-4 h-4" />
                                {isComplete ? 'Request New Check' : 'Re-issue Invite'}
                            </button>
                        )}
                    </div>

                    {latestCheck.isLinkActive && !isActionRequired && (
                        <p className="text-xs text-gray-400 mt-2">
                            Waiting for driver to complete identity verification. A new check cannot be requested until this one is resolved.
                        </p>
                    )}
                </div>
            )}

            {showRequestModal && (
                <RequestDbsModal
                    isOpen={showRequestModal}
                    onClose={() => setShowRequestModal(false)}
                    onConfirm={handleRequestDbs}
                    driver={driver}
                />
            )}

            {showInitiateModal && latestCheck && (
                <InitiateDbsModal
                    isOpen={showInitiateModal}
                    onClose={() => setShowInitiateModal(false)}
                    onConfirm={handleInitiateDbs}
                    check={latestCheck}
                    orgName={orgName}
                    currentUserName={currentUserName}
                />
            )}
        </div>
    );
};

export default DbsTab;
